"use client";

import React, { useMemo } from "react";
import Link from "next/link";
import { ResponsiveModal } from "@wtc/ui";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import { UniversalDetailActions } from "./UniversalDetailPage/UniversalDetailActions";
import { DetailHeaderContent } from "./UniversalDetailPage/components/DetailHeaderContent";
import { InlineEditableInfoPanel } from "./InlineEditableInfoPanel";
import { SectionedMobileDetailPanel } from "./UniversalCRMTable/mobile/SectionedMobileDetailPanel";
import { buildDetailHeaderEntityData } from "./UniversalDetailPage/utils/build-detail-header-entity-data";
import { cn } from "@/lib/utils";
import { DetailModalTabsBar } from "./DetailModalTabsBar";
import { useResizableSplit } from "./useResizableSplit";
import { useRoleCheck } from "@/lib/hooks/auth/useRoleCheck";
import { isTabAllowedForRole } from "@/lib/auth/tab-role-visibility";

import type { BaseEntity, SimpleTableConfig } from "./UniversalCRMTable/types/shared";
import type { TabConfig, DetailAction } from "./UniversalDetailPage/types/detailPage";

const LAYOUT_CONTAINER_SELECTOR = "[data-detail-modal-layout]";
const MOBILE_BREAKPOINT_PX = 1024;

export interface EntityDetailModalLayoutProps<T extends BaseEntity> {
  /** The entity item being displayed */
  item: T;
  /** Entity name (e.g., "contacts", "tickets") */
  entityName: string;
  /** Table configuration for the entity */
  config: SimpleTableConfig<T>;
  /** Header title to display */
  headerTitle: string;
  /** Optional header subtitle */
  headerSubtitle?: string;
  /** Actions to display in the header */
  actions: DetailAction<T>[];
  /** Tab configurations */
  tabs: TabConfig<T>[];
  /** Currently active tab ID */
  activeTab: string;
  /** Callback when tab changes */
  onTabChange: (tabId: string) => void;
  /** Render function for the primary area (left column) */
  renderPrimaryArea: (
    activeTab: string,
    item: T,
    onTagsUpdate?: (newTags: string[]) => Promise<void>,
    onInlineEdit?: (item: T) => Promise<void>
  ) => React.ReactNode;
  /** Render function for the info panel (right column). Ignored when onInlineEdit is provided. */
  renderInfoPanel: (item: T) => React.ReactNode;
  /** Optional custom renderer for the inline-edit right panel when onInlineEdit is enabled. */
  renderInlineInfoPanel?: (item: T, onInlineEdit: (item: T) => Promise<void>) => React.ReactNode;
  /** When provided, the info panel uses InlineEditableInfoPanel with expand-to-fullscreen. */
  onInlineEdit?: (item: T) => Promise<void>;
  /** Callback for updating tags in the header */
  onTagsUpdate?: (newTags: string[]) => Promise<void>;
  /** Whether the modal is open (for controlled state) */
  open?: boolean;
  /** Callback when modal should close */
  onOpenChange?: (open: boolean) => void;
  /** Global modal ID (for global modal stack) */
  globalModalId?: string;
  /** Additional CSS classes */
  className?: string;
  /** Navigation buttons (Previous/Next) */
  navigationButtons?: React.ReactNode;
  /** Full-width footer below the two columns (e.g. task comment composer) */
  modalFooter?: React.ReactNode;
}

function useMobileBreakpoint(breakpointPx: number): boolean {
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < breakpointPx);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [breakpointPx]);

  return isMobile;
}

export function EntityDetailModalLayout<T extends BaseEntity>({
  item,
  entityName,
  config,
  headerTitle,
  headerSubtitle,
  actions,
  tabs,
  activeTab,
  onTabChange,
  renderPrimaryArea,
  renderInfoPanel,
  renderInlineInfoPanel,
  onInlineEdit,
  onTagsUpdate,
  globalModalId,
  className = "",
  navigationButtons,
  modalFooter,
}: EntityDetailModalLayoutProps<T>) {
  const isMobile = useMobileBreakpoint(MOBILE_BREAKPOINT_PX);
  const { isClientUser } = useRoleCheck();

  // Client users get a READ-ONLY detail modal: CRM entities (contacts, etc.)
  // are editable only by WTC employees/admins. Suppressing onInlineEdit drops
  // every edit affordance (header field pencils, the inline-editable info
  // panel + its expand-to-edit button) so it renders the read-only panel.
  // Mutating quick actions are independently role-gated by UniversalDetailActions.
  const effectiveInlineEdit = isClientUser ? undefined : onInlineEdit;
  const { leftFraction, isDragging, handleResizeStart } = useResizableSplit(
    LAYOUT_CONTAINER_SELECTOR
  );

  const entityData = useMemo(() => {
    if (!item) return null;
    return buildDetailHeaderEntityData(item, config.entity);
  }, [item, config.entity]);

  const visibleTabs = useMemo(() => {
    return tabs.filter((tab) => {
      // Role gate: clients only see tabs explicitly marked clientVisible.
      // Employees/admins are unaffected (helper returns true for non-clients).
      if (!isTabAllowedForRole(tab, isClientUser)) return false;
      if (tab.visible && typeof tab.visible === "function") {
        return item ? tab.visible(item) : true;
      }
      return true;
    });
  }, [tabs, item, isClientUser]);

  // Ensure active tab is valid
  React.useEffect(() => {
    if (activeTab && !visibleTabs.some((tab) => tab.id === activeTab)) {
      onTabChange(visibleTabs[0]?.id || "details");
    }
  }, [activeTab, visibleTabs, onTabChange]);

  // Memoize the title node so its reference stays stable across renders.
  // Inline JSX here would create a new object every render, triggering
  // the drawer open/close effect repeatedly ("Maximum update depth exceeded").
  const modalTitle = useMemo(() => {
    if (item && entityData) {
      return (
        <DetailHeaderContent
          item={item}
          config={config}
          pageTitle={headerTitle}
          pageDescription={headerSubtitle || ""}
          entityData={entityData}
          onTagsUpdate={onTagsUpdate}
          onInlineEdit={effectiveInlineEdit}
        />
      );
    }
    return headerTitle;
  }, [item, entityData, config, headerTitle, headerSubtitle, onTagsUpdate, effectiveInlineEdit]);

  const showActionsBar =
    navigationButtons || actions.length > 0 || entityName === "tickets";

  const columnStyle = (fraction: number): React.CSSProperties | undefined =>
    !isMobile ? { width: `${fraction * 100}%`, minWidth: 0 } : undefined;

  const renderRightPanel = () => {
    if (
      effectiveInlineEdit &&
      !renderInlineInfoPanel &&
      isMobile &&
      config.mobileDetailSections &&
      config.mobileDetailSections.length > 0
    ) {
      return (
        <SectionedMobileDetailPanel item={item} config={config} onInlineEdit={effectiveInlineEdit} />
      );
    }
    if (effectiveInlineEdit && renderInlineInfoPanel) {
      return renderInlineInfoPanel(item, effectiveInlineEdit);
    }
    if (effectiveInlineEdit) {
      return (
        <InlineEditableInfoPanel
          item={item}
          config={config}
          onInlineEdit={effectiveInlineEdit}
          title={config.inlineInfoPanelTitle ?? `${config.displayName} Information`}
          showExpandButton
          variant="panel"
        />
      );
    }
    return renderInfoPanel(item);
  };

  return (
    <ResponsiveModal
      globalModalId={globalModalId || "entity-detail-modal"}
      title={modalTitle}
      maxWidth="90vw"
      className={cn("flex flex-col", className)}
    >
      {showActionsBar && (
        <div className="flex items-center justify-between gap-2 mb-4 -mx-4 px-4 md:-mx-6 md:px-6 pb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            {navigationButtons}
            {entityName === "tickets" && item?.id && (
              <Link href={`/tickets/edit/${item.id}`}>
                <Button variant="outline" size="sm" className="gap-2">
                  <ExternalLink className="h-4 w-4" />
                  Edit in full page
                </Button>
              </Link>
            )}
          </div>
          <UniversalDetailActions actions={actions} maxActions={3} />
        </div>
      )}

      <DetailModalTabsBar
        visibleTabs={visibleTabs}
        activeTab={activeTab}
        onTabChange={onTabChange}
        isMobile={isMobile}
      />

      <div
        data-detail-modal-layout
        className={cn(
          "flex flex-col lg:flex-row gap-0 min-h-0",
          isDragging && "select-none"
        )}
      >
        <div
          className="min-w-0 lg:overflow-y-auto lg:max-h-[calc(90vh-200px)] lg:flex-shrink-0"
          style={columnStyle(leftFraction)}
        >
          {renderPrimaryArea(activeTab, item, onTagsUpdate, effectiveInlineEdit)}
        </div>

        {!isMobile && (
          <button
            type="button"
            aria-label="Resize columns"
            onMouseDown={handleResizeStart}
            className={cn(
              "flex-shrink-0 w-2 lg:w-3 border-l border-r border-gray-200 dark:border-gray-700 hover:bg-blue-50 dark:hover:bg-blue-900/40 hover:border-blue-200 dark:hover:border-blue-800 cursor-col-resize transition-colors",
              isDragging && "bg-blue-100 dark:bg-blue-900/40 border-blue-300 dark:border-blue-800"
            )}
          />
        )}

        <div
          className="min-w-0 lg:overflow-y-auto lg:max-h-[calc(90vh-200px)] border-t lg:border-t-0 lg:border-l border-gray-200 dark:border-gray-700 pt-4 lg:pt-0 pl-0 lg:pl-6 lg:flex-shrink-0"
          style={columnStyle(1 - leftFraction)}
        >
          {renderRightPanel()}
        </div>
      </div>

      {modalFooter ? (
        <div className="mt-4 -mx-4 md:-mx-6 border-t border-gray-200 dark:border-gray-700 pt-4 shrink-0">{modalFooter}</div>
      ) : null}
    </ResponsiveModal>
  );
}

export default EntityDetailModalLayout;
